/* eslint-disable @typescript-eslint/no-unused-vars */
import type { Maybe } from '@backmarket/utils/types/Maybe'
import { z } from 'zod/mini'

import {
  LooseRichTextSchema,
  type LoosenRichTextFields,
} from '../types/loose-rich-text'
import type { Equal, ExpectTrue } from '../types/validation'

import { type CTALink, CTALinkSchema } from './cta-link'
import { type Image, ImageSchema } from './image'
import type { RichText } from './rich-text'
/* TypeScript */
export const STRUCTURED_DATA_TYPE = {
  JSON_ONLY: 'JSON_ONLY',
  HTML_ONLY: 'HTML_ONLY',
  JSON_AND_HTML: 'JSON_AND_HTML',
} as const

export type StructuredDataType =
  (typeof STRUCTURED_DATA_TYPE)[keyof typeof STRUCTURED_DATA_TYPE]

type FaqQuestionImage = Omit<Image, 'widthMobile' | 'heightMobile'>

export type FaqQuestion = {
  id: string
  title: string
  text: RichText
  image?: Maybe<FaqQuestionImage>
  state?: Maybe<'expanded' | 'collapsed'>
}

export type FaqApiProps = {
  technicalName?: Maybe<string>
  title: string
  subtitle?: Maybe<string>
  questions: FaqQuestion[]
  rendering?: Maybe<StructuredDataType>
  ctaLink?: Maybe<CTALink>
  whiteBackground?: Maybe<boolean>
}

/* Schemas */
export const StructuredDataTypeSchema = z.enum([
  'JSON_ONLY',
  'HTML_ONLY',
  'JSON_AND_HTML',
])

const FaqQuestionImageSchema = z.omit(ImageSchema, {
  widthMobile: true,
  heightMobile: true,
})

export const FaqQuestionSchema = z.object({
  id: z.string(),
  title: z.string(),
  text: LooseRichTextSchema,
  image: z.nullish(FaqQuestionImageSchema),
  state: z.nullish(z.enum(['expanded', 'collapsed'])),
})

export const FaqApiPropsSchema = z.object({
  technicalName: z.nullish(z.string()),
  title: z.string(),
  subtitle: z.nullish(z.string()),
  questions: z.array(FaqQuestionSchema),
  rendering: z.nullish(StructuredDataTypeSchema),
  ctaLink: z.nullish(CTALinkSchema),
  whiteBackground: z.nullish(z.boolean()),
})

/* Schemas validation */
type TestFaqQuestion = ExpectTrue<
  Equal<LoosenRichTextFields<FaqQuestion>, z.infer<typeof FaqQuestionSchema>>
>
type TestFaqApiProps = ExpectTrue<
  Equal<LoosenRichTextFields<FaqApiProps>, z.infer<typeof FaqApiPropsSchema>>
>
